import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  CollectionAsset,
  useCollectionGridSettings,
  useGridCollection,
  useProviderLogo,
} from '@kaltura-ott/tvpil-shared';

import { COMPONENT_NAME, PAGE_SIZE } from 'components/pages/Grids/constants';
import { useGridMatchPath } from 'components/pages/Grids/hooks/useGridMatchPath/useGridMatchPath';
import { DEFAULT_IMAGE_PARAMS, WIDGET_TYPE } from 'consts';
import { WidgetProviderData } from 'context/widgetProvider/types';
import { useLoadingPerformanceEvents } from 'hooks';
import { useRhinoErrorReaction } from 'hooks/common/useRhinoErrorReaction';
import routes from 'routesPaths';
import { APPLICATION_SPINNER_EVENT_NAME, applyStyleHexToHSL, applyStyles } from 'utils';

function useCollectionGrid() {
  const { type, id } = useGridMatchPath(routes.collectionGrid.path);
  const [, setForceUpdate] = useState(false); // For force updating of component
  const gridSettings = useCollectionGridSettings({ gridReferenceId: type, railReferenceId: id, ...DEFAULT_IMAGE_PARAMS });
  const providerLogo = useProviderLogo(gridSettings.settings?.providerName);
  const wrapperRef = useRef(null);
  const gridType = WIDGET_TYPE.COLLECTION;
  const intl = useIntl();
  const notValidPageTitle = intl.formatMessage({
    id: `${COMPONENT_NAME}.notValidPage`,
    defaultMessage: 'Not a valid page',
  });

  const { items, totalCount, isLoading, hasNextPage, fetchNextPage, error, refetch } = useGridCollection({
    gridReferenceId: type,
    railReferenceId: id,
    pageSize: PAGE_SIZE,
  });
  const data: WidgetProviderData<CollectionAsset> = {
    items,
    totalCount,
    hasNextPage,
    isLoading,
    error,
    fetchNextPage,
    refetch,
  };
  const shouldSendAppLoadingPerformanceEvent = isLoading && !data.items.length;

  useEffect(() => {
    async function initGridPage() {
      const node = wrapperRef?.current;

      if (node && gridSettings.settings?.branding!) {
        applyStyles(node, gridSettings.settings.branding, false, true);
        applyStyleHexToHSL(node, gridSettings.settings.branding.backgroundColor);
      }
    }

    if (gridSettings && !gridSettings.error) {
      initGridPage();
    }
  }, [type, id, gridSettings.isLoading]);


  useRhinoErrorReaction({ error: gridSettings.error, refetch: gridSettings.refetch });

  useLoadingPerformanceEvents(APPLICATION_SPINNER_EVENT_NAME.GRID_PAGE, shouldSendAppLoadingPerformanceEvent);

  return {
    shouldSendAppLoadingPerformanceEvent,
    gridSettings: gridSettings.settings,
    isLoading: gridSettings.isLoading,
    wrapperRef,
    providerLogo,
    setForceUpdate,
    notValidPageTitle,
    gridType,
    gridData: data,
  };
}

export { useCollectionGrid };
